<template>
  <div class="pricing-page">
    <!-- Hero -->
    <section class="pricing-hero">
      <div class="container">
        <div class="row justify-content-center text-center">
          <div class="col-lg-8 col-md-10">
            <p class="eyebrow">Pricing</p>
            <h1 class="hero-title pricing-hero-title">
              One plan. $50 a month minimum.<br />
              Pay for what you use.
            </h1>
            <p class="hero-sub">
              Every module, unlimited users, and unlimited locations from day one.
              Usage is itemized in cents per action; whenever it adds up to more
              than $50, you pay the usage. Never both.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Plan card -->
    <section class="plan-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-9">
            <div class="plan-card">
              <div class="plan-card-header">
                <div>
                  <p class="plan-eyebrow">All-in plan</p>
                  <h2 class="plan-title">Off The Couch</h2>
                </div>
                <div class="plan-price">
                  <span class="price-amount">$50</span>
                  <span class="price-suffix">/ month minimum</span>
                </div>
              </div>

              <p class="plan-blurb">
                If your usage adds up to more than $50 in a billing cycle, you pay the
                usage total. If it doesn't, you pay $50. That is the entire pricing rule,
                and your invoice reads like a receipt of what your venue actually did.
              </p>

              <div class="plan-cta">
                <a href="/contact" class="btn-primary-cta">Get a demo</a>
                <a href="https://console.offthecouch.io/login" class="btn-link-cta">
                  Or sign in <svg-icon name="arrow-right" :size="14" />
                </a>
              </div>

              <ul class="plan-includes">
                <li><svg-icon name="check" :size="16" /> Every feature unlocked from day one</li>
                <li><svg-icon name="check" :size="16" /> Unlimited staff users and locations</li>
                <li><svg-icon name="check" :size="16" /> Unlimited templates, dashboards, and reports</li>
                <li><svg-icon name="check" :size="16" /> Cancel anytime, no contracts</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Money zone: one table that is both the rate card and the calculator -->
    <section class="money-zone section-band section-band--tilt">
      <div class="container">
        <div class="row justify-content-center text-center mb-4">
          <div class="col-lg-8">
            <h2 class="section-title">Estimate your bill</h2>
            <p class="section-sub">
              Every rate is listed below. Put your own numbers in the middle column
              to see what a typical month costs, or start from an example.
            </p>
          </div>
        </div>

        <div class="row justify-content-center">
          <div class="col-lg-10">
            <div class="estimator-card">
              <img
                src="/img/pandas/calculator.png"
                alt=""
                class="estimator-panda"
                loading="lazy"
                width="120"
                height="120"
              />
              <div class="estimator-presets">
                <button
                  v-for="preset in presets"
                  :key="preset.name"
                  :class="['preset-chip', { active: activePreset === preset.name }]"
                  @click="applyPreset(preset)"
                >
                  {{ preset.name }}
                </button>
              </div>

              <div class="usage-table-wrap">
                <table class="usage-table">
                  <thead>
                    <tr>
                      <th>Feature</th>
                      <th>Rate</th>
                      <th class="text-center">Your month</th>
                      <th class="text-right">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in estimator" :key="item.key">
                      <td>
                        <div class="cell-feature">
                          <svg-icon :name="item.icon" :size="18" />
                          <span>{{ item.name }}</span>
                        </div>
                      </td>
                      <td class="cell-rate">
                        {{ item.rateLabel }}
                        <span v-if="item.note" class="cell-note">{{ item.note }}</span>
                      </td>
                      <td class="text-center">
                        <input
                          type="number"
                          min="0"
                          :step="item.step || 1"
                          v-model.number="item.qty"
                          class="estimator-qty"
                          :aria-label="item.name"
                          @input="activePreset = null"
                        />
                      </td>
                      <td class="text-right cell-price">${{ lineTotal(item) }}</td>
                    </tr>

                    <tr v-for="item in free" :key="item.name" class="row-free">
                      <td>
                        <div class="cell-feature">
                          <svg-icon :name="item.icon" :size="18" />
                          <span>{{ item.name }}</span>
                        </div>
                      </td>
                      <td class="cell-rate">
                        {{ item.unit }}
                      </td>
                      <td class="text-center cell-price cell-price-free">Free</td>
                      <td class="text-right cell-price cell-price-free">$0.00</td>
                    </tr>

                    <tr v-for="item in comingSoon" :key="item.name" class="row-free">
                      <td>
                        <div class="cell-feature">
                          <svg-icon :name="item.icon" :size="18" />
                          <span>{{ item.name }}</span>
                        </div>
                      </td>
                      <td class="cell-rate">{{ item.note }}</td>
                      <td class="text-center cell-tbd">Beta</td>
                      <td class="text-right cell-tbd">TBD</td>
                    </tr>

                    <!-- Text messaging bills on top of the minimum, never inside it,
                         so it has its own rows and its own line in the summary. -->
                    <tr v-for="item in sms" :key="item.key">
                      <td>
                        <div class="cell-feature">
                          <svg-icon :name="item.icon" :size="18" />
                          <span>{{ item.name }}</span>
                        </div>
                      </td>
                      <td class="cell-rate">
                        {{ item.rateLabel }}
                        <span v-if="item.note" class="cell-note">{{ item.note }}</span>
                      </td>
                      <td class="text-center">
                        <input
                          type="number"
                          min="0"
                          :step="item.step || 1"
                          v-model.number="item.qty"
                          class="estimator-qty"
                          :aria-label="item.name"
                        />
                      </td>
                      <td class="text-right cell-price">${{ smsLineTotal(item) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="estimator-summary">
                <div class="estimator-summary-row">
                  <span>Usage</span>
                  <strong>${{ usageTotal }}</strong>
                </div>
                <div class="estimator-summary-row">
                  <span>Monthly minimum</span>
                  <strong>$50.00</strong>
                </div>
                <div v-if="parseFloat(smsTotal) > 0" class="estimator-summary-row">
                  <span>Text messaging, on top of the minimum</span>
                  <strong>${{ smsTotal }}</strong>
                </div>
                <div class="estimator-summary-row estimator-summary-final">
                  <span>You'd pay</span>
                  <strong>${{ billedTotal }}</strong>
                </div>
                <p v-if="parseFloat(usageTotal) < 50" class="estimator-hint">
                  Usage is under the minimum, so the bill is a flat $50{{ parseFloat(smsTotal) > 0 ? ' plus text messaging' : '' }}.
                </p>
                <p v-else class="estimator-hint">
                  Usage covers the minimum, so you pay usage exactly{{ parseFloat(smsTotal) > 0 ? ', plus text messaging' : '' }}. Never the minimum and usage both.
                </p>
              </div>

              <p class="rates-footnote">
                Trigger emails like confirmations, reminders, and receipts are always free.
                Gift card redemptions are a payment method, not a transaction.
                Text messaging has a one-time $20 activation per location for carrier registration.
                <a href="/legal/rate-card">See the full rate card &rarr;</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing principles: the philosophy, right after the numbers -->
    <section class="principles-section">
      <div class="container">
        <div class="row justify-content-center text-center mb-4">
          <div class="col-lg-8">
            <h2 class="section-title">Why we price this way</h2>
          </div>
        </div>
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <div class="usage-card free-card">
              <div class="free-grid">
                <div v-for="item in principles" :key="item.name" class="free-item">
                  <svg-icon :name="item.icon" :size="22" />
                  <div>
                    <h4>{{ item.name }}</h4>
                    <p>{{ item.note }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ: objections handled, right before the ask -->
    <section class="faq-section">
      <div class="container">
        <div class="row justify-content-center text-center mb-4">
          <div class="col-lg-8">
            <h2 class="section-title">Common questions</h2>
          </div>
        </div>
        <div class="row justify-content-center">
          <div class="col-lg-9">
            <div class="faq-grid">
              <div class="faq-item">
                <h4>What does the $50 minimum include?</h4>
                <p>Everything: every module, unlimited staff users and roles, unlimited locations, unlimited templates and dashboards, data storage, and support. There is nothing to unlock and no upgrade tier.</p>
              </div>
              <div class="faq-item">
                <h4>What counts as a transaction?</h4>
                <p>A transaction is a single receipt, no matter how many purchases are included. If a customer books one event, that's one transaction. If they book three events, buy a gift card, add two bottles of water, and purchase a glitter add-on on the same receipt, that's still just one transaction.</p>
                <p><strong>Each completed receipt counts as one transaction, not each individual item or purchase.</strong></p>
              </div>
              <div class="faq-item">
                <h4>What happens in my slow season?</h4>
                <p>Your bill shrinks with your calendar. Usage is only charged when bookings, waivers, and photos actually happen, so an off-season month settles back to the $50 minimum. No annual commitment, cancel any time.</p>
              </div>
              <div class="faq-item">
                <h4>Are confirmation and reminder emails free?</h4>
                <p>Yes. Trigger emails (booking confirmations, payment receipts, reminders) are consequences of transactions you already paid for. You only pay for marketing emails you choose to send.</p>
              </div>
              <div class="faq-item">
                <h4>What happens if I have multiple locations?</h4>
                <p>Nothing changes. All your locations share one $50 minimum, and usage is metered the same way everywhere. Adding a location adds zero fixed cost.</p>
              </div>
              <div class="faq-item">
                <h4>Can I import data from another system?</h4>
                <p>Yes. We help migrate your existing customers, bookings, and historical data so reports work from day one.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- End-of-page CTA band -->
    <section class="cta-band">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-8">
            <h2 class="cta-band-title">Ready to see it in action?</h2>
            <p class="cta-band-sub">
              We'll walk through the platform live and answer questions specific to your venue's setup.
            </p>
            <div class="cta-band-buttons">
              <a href="/contact" class="btn-cta-primary">Get a demo</a>
              <a href="/features" class="btn-cta-secondary">Browse features &rarr;</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
// PRICING MODEL: greatest($50, usage). $50 monthly minimum; when itemized usage
// exceeds $50 the customer pays the usage total (corrected by Jana 2026-07-03,
// NOT additive). Rates below are the real published rates as of the 2026-08-15
// pricing sheet. Keep them in sync with /legal/rate-card, which restates the
// same numbers in plain tabulated form.
const MONTHLY_MIN = 50;

export default {
  name: "Pricing",
  data() {
    return {
      monthlyMin: MONTHLY_MIN,
      activePreset: 'Small venue',
      // Real rates as of 2026-08. Beta items have no published rate yet.
      estimator: [
        { key: 't',  name: 'Transactions',     icon: 'shopping-cart',  price: 0.25,  rateLabel: '$0.25 each',        note: 'one completed receipt', qty: 150 },
        { key: 'g',  name: 'Event instances',  icon: 'gamepad',        price: 0.06,  rateLabel: '$0.06 each',        note: 'one tracked event run', qty: 100 },
        { key: 'p',  name: 'Photos',           icon: 'camera',         price: 0.10,  rateLabel: '$0.10 each',        note: 'includes long-term storage', qty: 80 },
        { key: 'w',  name: 'Waivers',          icon: 'file',           price: 0.02,  rateLabel: '$0.02 each',        note: 'every waiver on file, signed online or added by staff', qty: 100 },
        { key: 's',  name: 'Survey responses', icon: 'message-square', price: 0.02,  rateLabel: '$0.02 each',        note: 'invitation emails included', qty: 50 },
        { key: 'c',  name: 'Checklists',       icon: 'check-circle',   price: 0.02,  rateLabel: '$0.02 each',        qty: 0 },
        { key: 'e',  name: 'Marketing emails', icon: 'mail',           price: 0.001, rateLabel: '$1 per 1,000',      note: 'no contact or campaign limits', qty: 0, step: 100 },
        { key: 'api', name: 'Community API',   icon: 'layout',         price: 0.003, rateLabel: '$3 per 1,000',      note: 'first 3,000 requests free each month', qty: 0, step: 100, freeAllowance: 3000 },
      ],
      free: [
        { name: 'Analytics & dashboards', icon: 'bar-chart', unit: 'always on' },
        { name: 'Trigger emails',         icon: 'bell',      unit: 'confirmations, reminders, receipts' },
        { name: 'Time tracking',          icon: 'clock',     unit: 'with project management' },
      ],
      // Text messaging (US only). Billed on top of the monthly minimum, never inside
      // it: $10 per active number per month with 250 credits included per number, then
      // $0.03 per credit. One credit is one message segment, sent or received.
      sms: [
        { key: 'smsn', name: 'Text messaging numbers', icon: 'message-square', price: 10,   rateLabel: '$10 per number per month', note: '250 credits included per number, billed on top of the minimum', qty: 0 },
        { key: 'smss', name: 'Text messages',          icon: 'message-square', price: 0.03, rateLabel: '$0.03 per credit',           note: 'beyond the included 250 per number. One credit is one message segment, sent or received', qty: 0, step: 50 },
      ],
      comingSoon: [
        { name: 'Project management', icon: 'layout',  note: 'beta pricing, rate not yet set' },
        // MMS and WhatsApp are on the roadmap but not offered yet, so they stay off the page
        // { name: 'MMS',                icon: 'camera',  note: 'beta pricing, rate not yet set' },
        // { name: 'WhatsApp',           icon: 'mail',    note: 'beta pricing, rate not yet set' },
      ],
      presets: [
        // qty order matches the estimator rows above. Small venue intentionally
        // lands just above the $50 floor so the default state demonstrates
        // pay-for-what-you-use rather than a flat fee.
        { name: 'Quiet month', qtys: [40, 35, 20, 30, 10, 0, 0, 0] },
        { name: 'Small venue', qtys: [150, 100, 80, 100, 50, 0, 0, 0] },
        { name: 'Busy venue',  qtys: [500, 450, 350, 400, 200, 600, 2000, 5000] },
      ],
      principles: [
        { name: 'No cut of your revenue', icon: 'shield', note: 'We never take a percentage of your ticket sales, and we never pass booking fees to your customers. Your success is yours.' },
        { name: 'Priced at the action',   icon: 'zap',    note: 'Each rate maps to a real event: a booking made, a photo delivered, a waiver on file. You control the bill by running your venue, not by negotiating a contract.' },
        { name: 'Built to be used whole', icon: 'layers', note: 'The booking feeds the waiver, the event feeds the survey, the survey feeds the email. Everything is on because buying it in pieces would mean using it wrong.' },
      ],
    };
  },
  computed: {
    usageTotal() {
      // Round per line, then sum, so the total always equals the sum of the
      // visible line totals (same discipline as the billing backend).
      const cents = this.estimator.reduce((acc, i) => {
        const line = Math.round(Number(parseFloat(this.billableQty(i) * i.price) + 'e2'));
        return acc + line;
      }, 0);
      return (cents / 100).toFixed(2);
    },
    // Text messaging never counts toward the minimum; it is added after the greater-of
    smsTotal() {
      const cents = this.sms.reduce((acc, i) => acc + Math.round(Number(parseFloat(this.smsBillableQty(i) * i.price) + 'e2')), 0);
      return (cents / 100).toFixed(2);
    },
    billedTotal() {
      const cents = Math.round(Number(Math.max(this.monthlyMin, parseFloat(this.usageTotal)) + 'e2')) + Math.round(Number(parseFloat(this.smsTotal) + 'e2'));
      return (cents / 100).toFixed(2);
    },
  },
  methods: {
    billableQty(item) {
      const qty = Number(item.qty) || 0;
      return Math.max(0, qty - (item.freeAllowance || 0));
    },
    lineTotal(item) {
      return (this.billableQty(item) * item.price).toFixed(2);
    },
    // Credits are included per number: 250 times the number count comes off the segments
    smsBillableQty(item) {
      const qty = Number(item.qty) || 0;
      if (item.key !== 'smss') return qty;
      const numbers = Number((this.sms.find((i) => i.key === 'smsn') || {}).qty) || 0;
      return Math.max(0, qty - 250 * numbers);
    },
    smsLineTotal(item) {
      return (this.smsBillableQty(item) * item.price).toFixed(2);
    },
    applyPreset(preset) {
      this.activePreset = preset.name;
      preset.qtys.forEach((q, i) => {
        if (this.estimator[i]) this.estimator[i].qty = q;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/marketing-tokens';

.pricing-page {
  padding-top: 120px;
  padding-bottom: 80px;
}

// Three sentences is a long headline; size it so the hard break lands two
// balanced rows instead of a ragged third line
.pricing-hero-title {
  font-size: 2.6rem;

  @media (max-width: 991px) {
    font-size: 2rem;
  }

  @media (max-width: 575px) {
    font-size: 1.7rem;
    br { display: none; }
  }
}

.plan-eyebrow {
  @extend .eyebrow;
  margin-bottom: 0;
}

.hero-sub {
  font-size: 1.125rem;
  color: $muted;
  line-height: 1.6;
}

/* Plan card */

/* The plan card completes the hero's sentence; keep them close */
.plan-section { margin-top: 2.25rem; }

.plan-card {
  background: linear-gradient(135deg, rgba(225, 78, 202, 0.08), rgba(102, 81, 145, 0.08));
  border: 1px solid $primary-border;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
}

.plan-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.plan-title {
  font-size: 2rem;
  color: white;
  margin: 0;
  font-weight: 700;
}

.plan-price {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.price-amount {
  font-size: 3.5rem;
  font-weight: 800;
  color: white;
  line-height: 1;
}

.price-suffix {
  color: $muted;
  font-size: 0.95rem;
}

.plan-blurb {
  color: $muted;
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 1.75rem;
}

.plan-cta {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;
}

.btn-primary-cta {
  display: inline-block;
  background: $primary;
  color: white;
  padding: 0.85rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 12px 30px rgba(225, 78, 202, 0.35);
    text-decoration: none;
  }
}

.btn-link-cta {
  color: white;
  font-weight: 500;
  text-decoration: none;
  &:hover { color: $primary; text-decoration: none; }
}

.plan-includes {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  color: $muted;
  font-size: 0.95rem;
  // Included markers: teal, the "you get this" accent
  ::v-deep .svg-icon { color: $teal; margin-right: 0.5rem; }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

/* Rhythm: the money zone is one padded band; lighter gaps elsewhere */
.money-zone {
  margin-top: 4rem;
  padding: 4rem 0;
}

.principles-section,
.faq-section {
  margin-top: 4rem;
}

/* Usage table */

.usage-card {
  background: $card-bg;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 1.5rem;
}

.usage-table-wrap {
  overflow-x: auto;
}

.usage-table {
  width: 100%;
  border-collapse: collapse;
  color: $muted;
  font-size: 0.95rem;

  thead th {
    text-align: left;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    padding: 0 1rem 0.75rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    &.text-right { text-align: right; }
    &.text-center { text-align: center; }
  }

  tbody td {
    padding: 1.25rem 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    vertical-align: middle;
    &.text-right { text-align: right; }
    &.text-center { text-align: center; }
  }

  tbody tr:last-child td { border-bottom: none; }
}

.cell-feature {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: white;
  font-weight: 600;
  font-size: 1rem;
  // Informational icons: match the estimator rows in the same band
  ::v-deep .svg-icon { color: $decorative; }
}

.cell-price {
  color: white;
  font-weight: 600;
  font-size: 1rem;
  white-space: nowrap;
}

.cell-price-free {
  color: $teal;
  font-weight: 600;
}

.rates-footnote {
  color: $muted-2;
  font-size: 0.85rem;
  line-height: 1.5;
  margin: 1.25rem 0 0;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);

  a {
    color: $primary;
    white-space: nowrap;

    &:hover { text-decoration: underline; }
  }
}

// Rate + note stack inside the combined table
.cell-rate {
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.85);
}

.cell-note {
  display: block;
  color: $muted-2;
  font-size: 0.76rem;
  line-height: 1.4;
  white-space: normal;
}

.cell-tbd {
  color: $muted-2;
  font-size: 0.85rem;
}

.row-free .cell-feature { color: $muted; }

/* Free grid */

.free-card { background: rgba(46, 201, 183, 0.05); border-color: rgba(46, 201, 183, 0.18); }

.free-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.25rem;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
}

.free-item {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  // Informational accent: teal, not pink
  ::v-deep .svg-icon {
    color: $teal;
    margin-top: 0.15rem;
    flex-shrink: 0;
  }
  h4 {
    color: white;
    font-size: 1rem;
    font-weight: 600;
    margin: 0 0 0.25rem;
  }
  p {
    color: $muted-2;
    font-size: 0.85rem;
    margin: 0;
    line-height: 1.45;
  }
}

/* Estimator */

.estimator-card {
  position: relative;
  background: $card-bg;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 2rem;
}

.estimator-panda {
  position: absolute;
  top: 1.25rem;
  right: 1.75rem;

  @media (max-width: 767px) { display: none; }
}

.estimator-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0 0 1.5rem;
}

.preset-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.02);
  color: $muted;
  font-size: 0.82rem;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  &:hover {
    color: white;
    border-color: $primary-border;
  }

  &.active {
    color: $primary;
    background: $primary-soft;
    border-color: $primary-border;
  }
}

.estimator-qty {
  width: 110px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid $primary-border;
  border-radius: 6px;
  color: white;
  padding: 0.5rem 0.75rem;
  font-size: 0.95rem;
  text-align: right;
  appearance: textfield;
  -moz-appearance: textfield;
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &:focus { border-color: $primary; }
}

.estimator-summary {
  background: rgba(225, 78, 202, 0.06);
  border: 1px solid rgba(225, 78, 202, 0.18);
  border-radius: 8px;
  padding: 1.25rem 1.5rem;
}

.estimator-summary-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.35rem 0;
  color: $muted;
  strong { color: white; font-weight: 600; }
}

.estimator-summary-final {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 0.5rem;
  padding-top: 0.85rem;
  color: white;
  font-size: 1.1rem;
  strong { font-size: 1.5rem; color: $primary; }
}

.estimator-hint {
  margin: 0.75rem 0 0;
  color: $muted-2;
  font-size: 0.85rem;
  text-align: center;
}

/* FAQ */

.faq-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
}

.faq-item {
  background: $card-bg;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 1.5rem;
  h4 { color: white; font-size: 1rem; font-weight: 600; margin: 0 0 0.5rem; }
  p { color: $muted; font-size: 0.9rem; margin: 0; line-height: 1.55; }
}

/* Mobile tuning */

@media (max-width: 768px) {
  .pricing-page { padding-top: 90px; }
  .plan-card { padding: 1.75rem; }
  .price-amount { font-size: 2.75rem; }
}

// The combined rate + calculator table is four columns wide. Rather than let
// it scroll sideways on a phone, tighten it and drop the supporting notes,
// which are repeated in full on the rate card page.
@media (max-width: 640px) {
  .estimator-card { padding: 1.25rem; }

  .usage-table {
    font-size: 0.8rem;

    thead th { padding: 0 0.4rem 0.6rem; font-size: 0.65rem; letter-spacing: 0.04em; }
    tbody td { padding: 0.85rem 0.4rem; }
  }

  .cell-feature {
    gap: 0.4rem;
    ::v-deep .svg-icon { display: none; }
  }

  .cell-note { display: none; }

  .cell-rate { white-space: normal; }

  .cell-price { font-size: 0.85rem; }

  .estimator-qty { width: 100%; max-width: 62px; padding: 0.35rem 0.4rem; }
}
</style>
